/* Where a person was in a list when they opened a record.
 *
 * A record is a page now rather than a panel over the list, and the reason the
 * panel existed still has to be met: closing a record returns you to your
 * place and your scroll position, not to the top of a re-fetched table
 * (docs/ux-decisions.md). The list's rows are already in the query cache for
 * five minutes, so the data is there at once. This remembers the rest: how far
 * down the page they were and which row they opened, keyed by the list's own
 * address, so a different search or page is a different place.
 *
 * sessionStorage, because it is this tab's own history and nothing else's. */

type Place = { scrollY: number; openId: string | null };

const PREFIX = "evie.list-place.";

export function rememberListPlace(key: string, place: Place): void {
  try {
    window.sessionStorage.setItem(PREFIX + key, JSON.stringify(place));
  } catch {
    // Storage unavailable costs the scroll position and nothing else.
  }
}

/** The remembered place, once. Reading it forgets it, so a fresh visit to the
 *  same list starts at the top. */
export function recallListPlace(key: string): Place | null {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    window.sessionStorage.removeItem(PREFIX + key);
    const parsed = JSON.parse(raw) as Partial<Place>;
    return {
      scrollY: typeof parsed.scrollY === "number" ? parsed.scrollY : 0,
      openId: typeof parsed.openId === "string" ? parsed.openId : null,
    };
  } catch {
    return null;
  }
}
